import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { auth } from "../../firebase";
import { getSavedPassengers, savePassengersToProfile } from "../../services/api";
import { useAuthState } from "react-firebase-hooks/auth";
import FlightCard from "./FlightCard";
import RoundTripFlightCard from "./RoundTripFlightCard";
import MultiCityFlightCard from "./MultiCityFlightCard";
import PassengerForm, { PassengerData } from "./PassengerForm";
import { FaClipboardList } from "react-icons/fa";
import ProceedToPaymentButton from "../ui/ProceedToPaymentButton";
import ReviewAndPayPopup from "./ReviewAndPayPopup";
import FareRulesDisplay from "./FareRulesDisplay";
import SeatMap, { SeatSelection } from "./SeatMap";
import PreBookingChecklist from "./PreBookingChecklist";
import { ChecklistItem } from "../../services/api";
import { useBooking } from "../../context/BookingContext";
import { serviceFeeOf } from "../../utils/pricing";
import styles from "../../styles";

function getPassengerTypes(passengerCounts: {
  ADT?: number;
  CHD?: number;
  INF?: number;
}) {
  const types: ("ADT" | "CHD" | "INF")[] = [];
  for (let i = 0; i < (passengerCounts?.ADT || 0); i++) types.push("ADT");
  for (let i = 0; i < (passengerCounts?.CHD || 0); i++) types.push("CHD");
  for (let i = 0; i < (passengerCounts?.INF || 0); i++) types.push("INF");
  return types;
}

const extractDepartureDate = (flightData: any): string | undefined => {
  try {
    // New format: extract from legs[]
    const legs: any[] = flightData?.legs ?? [];
    const outboundLeg = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
    if (outboundLeg?.departure?.dateTime) {
      const dt = outboundLeg.departure.dateTime;
      if (typeof dt === "string") {
        const m = dt.match(/^\d{4}-\d{2}-\d{2}/);
        if (m) return m[0];
      }
    }
    // Old format fallback: onwardFlight
    const onwardSchedules = flightData?.onwardFlight?.schedules;
    if (onwardSchedules && onwardSchedules.length > 0) {
      const departureTime = onwardSchedules[0]?.departure?.time;
      if (departureTime) {
        const dateMatch = flightData?.onwardFlight?.departureDateTime?.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) return dateMatch[1];
        const isoDate = new Date(departureTime);
        if (!isNaN(isoDate.getTime())) return isoDate.toISOString().split("T")[0];
      }
    }
    // Multicity: segments (old) or legs (new)
    const segments = flightData?.segments;
    if (Array.isArray(segments) && segments.length > 0) {
      const firstDep = segments[0]?.departureDateTime || segments[0]?.schedules?.[0]?.departure?.time;
      if (firstDep && typeof firstDep === "string") {
        const m = firstDep.match(/^\d{4}-\d{2}-\d{2}/);
        if (m) return m[0];
        const d = new Date(firstDep);
        if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
      }
    }
    return undefined;
  } catch (error) {
    console.error("Error extracting departure date:", error);
    return undefined;
  }
};

const extractReturnDate = (flightData: any): string | undefined => {
  try {
    // New format: inbound leg
    const legs: any[] = flightData?.legs ?? [];
    const inboundLeg = legs.find((l: any) => l.direction === "inbound");
    if (inboundLeg?.departure?.dateTime) {
      const dt = inboundLeg.departure.dateTime;
      if (typeof dt === "string") {
        const m = dt.match(/^\d{4}-\d{2}-\d{2}/);
        if (m) return m[0];
      }
    }
    // Old format fallback: returnFlight
    const retDateTime = flightData?.returnFlight?.departureDateTime;
    if (retDateTime && typeof retDateTime === "string") {
      const m = retDateTime.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) return m[0];
      const d = new Date(retDateTime);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
    }
    const returnSchedules = flightData?.returnFlight?.schedules;
    if (returnSchedules?.length > 0 && returnSchedules[0]?.departure?.time) {
      const d = new Date(returnSchedules[0].departure.time);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
};

const PassengerDetail = () => {
  const location = useLocation();
  const [user] = useAuthState(auth);

  const {
    revalId: contextRevalId,
    sessionId: contextSessionId,
    flightData: contextFlightData,
    passengerCounts: contextPassengerCounts,
    departureDate: contextDepDate,
    returnDate: contextRetDate,
    multicitySegmentDates: contextSegDates,
    tripType: contextTripType,
  } = useBooking();

  const flightData = location.state?.flightData ?? contextFlightData;
  const passengerCounts = location.state?.passengerCounts ?? contextPassengerCounts;
  const classOfService = location.state?.classOfService || "Y";
  const selectedClass = location.state?.selectedClass || "w";
  const revalId = contextRevalId || flightData?.revalId;

  const normalizeDate = (v: any): string | undefined =>
    v == null ? undefined : typeof v === "string" ? v.split("T")[0] : undefined;

  const departureDate =
    contextDepDate ||
    normalizeDate(location.state?.departureDate) ||
    extractDepartureDate(flightData) ||
    "";
  const returnDate =
    contextRetDate ??
    normalizeDate(location.state?.returnDate) ??
    extractReturnDate(flightData);
  const multicitySegmentDates =
    contextSegDates ??
    location.state?.multicitySegmentDates ??
    (Array.isArray(flightData?.legs) && flightData.legs.length > 2
      ? flightData.legs
        .map(
          (leg: any) =>
            leg?.departure?.dateTime?.match?.(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? ""
        )
        .filter(Boolean)
      : Array.isArray(flightData?.segments)
        ? flightData.segments
          .map(
            (s: any) =>
              s?.departureDateTime?.match?.(/^\d{4}-\d{2}-\d{2}/)?.[0] ||
              (s?.schedules?.[0]?.departure?.time
                ? new Date(s.schedules[0].departure.time).toISOString().split("T")[0]
                : "")
          )
          .filter(Boolean)
        : undefined);
  const tripType =
    contextTripType ||
    location.state?.tripType ||
    flightData?.tripType ||
    "oneway";

  const isRoundTrip = tripType === "roundtrip";
  const amount = flightData?.pricingInfo?.total ?? flightData?.totalFare?.amount ?? 0;
  const baseFare = flightData?.pricingInfo?.airlineBaseFare ?? flightData?.pricingInfo?.baseFare ?? flightData?.totalFare?.baseFare ?? 0;
  const taxes = flightData?.pricingInfo?.taxes ?? flightData?.totalFare?.taxes ?? 0;
  const currency = flightData?.pricingInfo?.currency ?? flightData?.totalFare?.currency ?? "AED";
  // Same derivation as every other screen. Post-revalidation there is no tier
  // to unwrap, so this resolves to the itinerary's fee — which by then is the
  // fee for the fare actually being booked.
  const serviceFee = serviceFeeOf(flightData);
  const airlineCode = flightData?.validatingCarrier?.code ?? flightData?.validatingCarrier ?? "";

  // New format: derive legs; Old format: onwardFlight/returnFlight
  const legs: any[] = flightData?.legs ?? [];
  const outboundLeg = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
  const inboundLeg = legs.find((l: any) => l.direction === "inbound");

  // Old format refs
  const onwardFlight = flightData?.onwardFlight;
  const returnFlight = flightData?.returnFlight;
  const onwardSchedules = onwardFlight?.schedules ?? [];
  const returnSchedules = returnFlight?.schedules ?? [];

  // Pick first segment from new format or old format
  const firstOnward = outboundLeg?.segments?.[0] ?? onwardSchedules[0];
  const lastOnward = outboundLeg?.segments?.[(outboundLeg?.segments?.length ?? 1) - 1] ?? onwardSchedules[onwardSchedules.length - 1];
  const firstReturn = inboundLeg?.segments?.[0] ?? returnSchedules[0];
  const lastReturn = inboundLeg?.segments?.[(inboundLeg?.segments?.length ?? 1) - 1] ?? returnSchedules[returnSchedules.length - 1];

  const parseTime = (dt: string | undefined) => {
    if (!dt) return "-";
    const m = dt.match(/T(\d{2}:\d{2})/);
    return m ? m[1] : dt;
  };

  const onwardProps = {
    flightNumber: firstOnward?.marketingCarrier
      ? `${firstOnward.marketingCarrier.code ?? ""} ${firstOnward.marketingCarrier.flightNumber ?? ""}`
      : `${firstOnward?.flightCode ?? ""} ${firstOnward?.flightNumber ?? ""}`,
    airlineName: airlineCode,
    airlineLogo: `/assets/airlines/${airlineCode}.png`,
    from: outboundLeg?.departure?.airport?.code ?? onwardFlight?.departureAirport ?? "-",
    to: outboundLeg?.arrival?.airport?.code ?? onwardFlight?.arrivalAirport ?? "-",
    departure: parseTime(outboundLeg?.departure?.dateTime) !== "-"
      ? parseTime(outboundLeg?.departure?.dateTime)
      : (firstOnward?.departure?.time ?? "-"),
    arrival: parseTime(outboundLeg?.arrival?.dateTime) !== "-"
      ? parseTime(outboundLeg?.arrival?.dateTime)
      : (lastOnward?.arrival?.time ?? "-"),
    classType: classOfService,
    flightData,
    selectedClass,
  };

  const hasReturn = inboundLeg || returnFlight;
  const returnProps = hasReturn
    ? {
      flightNumber: firstReturn?.marketingCarrier
        ? `${firstReturn.marketingCarrier.code ?? ""} ${firstReturn.marketingCarrier.flightNumber ?? ""}`
        : `${firstReturn?.flightCode ?? ""} ${firstReturn?.flightNumber ?? ""}`,
      airlineName: airlineCode,
      airlineLogo: `/assets/airlines/${airlineCode}.png`,
      from: inboundLeg?.departure?.airport?.code ?? returnFlight?.departureAirport ?? "-",
      to: inboundLeg?.arrival?.airport?.code ?? returnFlight?.arrivalAirport ?? "-",
      departure: parseTime(inboundLeg?.departure?.dateTime) !== "-"
        ? parseTime(inboundLeg?.departure?.dateTime)
        : (firstReturn?.departure?.time ?? "-"),
      arrival: parseTime(inboundLeg?.arrival?.dateTime) !== "-"
        ? parseTime(inboundLeg?.arrival?.dateTime)
        : (lastReturn?.arrival?.time ?? "-"),
      classType: classOfService,
      flightData,
      selectedClass,
    }
    : undefined;

  const totalPassengers =
    (passengerCounts?.ADT || 0) +
    (passengerCounts?.CHD || 0) +
    (passengerCounts?.INF || 0);

  const passengerTypes = getPassengerTypes(passengerCounts);

  const [passengers, setPassengers] = useState<PassengerData[]>(
    passengerTypes.map((type) => ({
      title: "",
      firstName: "",
      lastName: "",
      countryCode: "",
      phone: "",
      email: "",
      ffNumber: "",
      ffAirline: "",
      contactDeclined: false,
      mealPreference: "",
      assistance: "",
      seatPreference: "",
      passportNumber: "",
      dob: "",
      expiryDate: "",
      issuingCountry: "",
      agreed: false,
      insured: false,
      passengerType: type,
    }))
  );

  // Seat selection is off for phase 1: a chargeable seat is an ancillary and
  // needs an EMD to actually be bought, which this deployment cannot issue.
  // Mirrors SEAT_SELECTION_ENABLED on the server, which is what actually
  // refuses it — this only decides what the screen offers.
  const seatsEnabled = process.env.REACT_APP_SEAT_SELECTION_ENABLED === "true";

  const [showSeatMap, setShowSeatMap] = useState(false);
  const [seatSelections, setSeatSelections] = useState<SeatSelection[]>([]);

  const [savedPassengers, setSavedPassengers] = useState<PassengerData[]>([]);
  useEffect(() => {
    const fetchSavedPassengers = async () => {
      if (!user) return;
      try {
        const passengers = await getSavedPassengers(user.uid);
        setSavedPassengers(passengers);
      } catch (err) {
        console.error("[PassengerDetail] Failed to fetch saved passengers:", err);
      }
    };
    fetchSavedPassengers();
  }, [user]);

  const [selectedSaved, setSelectedSaved] = useState<(number | null)[]>(
    Array(totalPassengers).fill(null)
  );
  const [editingOverride, setEditingOverride] = useState<boolean[]>(
    Array(totalPassengers).fill(false)
  );
  const [saveToProfile, setSaveToProfile] = useState<boolean[]>(
    Array(totalPassengers).fill(false)
  );

  useEffect(() => {
    setSelectedSaved(Array(totalPassengers).fill(null));
    setEditingOverride(Array(totalPassengers).fill(false));
    setSaveToProfile(Array(totalPassengers).fill(false));
  }, [totalPassengers]);

  const handlePassengerChange = (idx: number, data: PassengerData) => {
    const updated = [...passengers];
    updated[idx] = { ...data, passengerType: passengerTypes[idx] };
    setPassengers(updated);
  };

  const handleSaveToProfile = async (idx: number) => {
    if (!user) return;
    const passenger = { ...passengers[idx] };
    if (
      savedPassengers.some(
        (p) =>
          p.passportNumber &&
          passenger.passportNumber &&
          p.passportNumber === passenger.passportNumber
      )
    ) {
      alert("Passenger with this passport number already exists.");
      return;
    }
    const updated = [...savedPassengers, passenger];
    setSavedPassengers(updated);
    setSelectedSaved((prev) =>
      prev.map((v, i) => (i === idx ? updated.length - 1 : v))
    );
    setSaveToProfile((prev) => prev.map((v, i) => (i === idx ? false : v)));
    try {
      await savePassengersToProfile(user.uid, updated);
    } catch (err) {
      console.error("[PassengerDetail] Failed to save passenger to profile:", err);
    }
  };

  let passengerIndex = 0;
  const renderPassengerForms = (
    label: string,
    type: "ADT" | "CHD" | "INF",
    count: number
  ) => {
    if (count <= 0) return null;
    return (
      <div className={styles.spacing.md}>
        {Array.from({ length: count }).map((_, i) => {
          const idx = passengerIndex++;
          const selectedIdx = selectedSaved[idx];
          const isSaved = selectedIdx !== null && savedPassengers[selectedIdx];
          const isEditing = editingOverride[idx];
          const passengerData = passengers[idx];

          const filteredPassengers = savedPassengers.filter(
            (p) => p.passengerType === type
          );

          return (
            <div
              key={idx}
              className="bg-[#F6F8FC] border border-[#E4E4E4] p-4 sm:p-5 rounded-2xl shadow-sm space-y-4"
            >
              <div className="mb-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Select Saved Passenger
                </label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm sm:text-base"
                  value={selectedIdx ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedSaved((prev) =>
                      prev.map((v, j) =>
                        j === idx ? (val === "" ? null : Number(val)) : v
                      )
                    );
                    if (val === "") {
                      handlePassengerChange(idx, {
                        title: "",
                        firstName: "",
                        lastName: "",
                        phone: "",
                        email: "",
                        ffNumber: "",
                        ffAirline: "",
                        contactDeclined: false,
                        mealPreference: "",
                        assistance: "",
                        seatPreference: "",
                        countryCode: "",
                        passportNumber: "",
                        dob: "",
                        expiryDate: "",
                        issuingCountry: "",
                        agreed: false,
                        insured: false,
                        passengerType: type,
                      });
                      // Reset editing override when changing selection
                      setEditingOverride((prev) =>
                        prev.map((v, j) => (j === idx ? false : v))
                      );
                    } else {
                      const selectedPassenger = filteredPassengers[Number(val)];
                      if (!selectedPassenger) return;
                      if (selectedPassenger.passengerType !== type) {
                        alert(
                          `Please select a saved passenger of type ${type}.`
                        );
                        setSelectedSaved((prev) =>
                          prev.map((v, j) => (j === idx ? null : v))
                        );
                        return;
                      }
                      handlePassengerChange(idx, selectedPassenger);
                      // Reset editing override when selecting a new saved passenger
                      setEditingOverride((prev) =>
                        prev.map((v, j) => (j === idx ? false : v))
                      );
                    }
                  }}
                >
                  <option value="">-- Enter New --</option>
                  {filteredPassengers.map((p, sidx) => (
                    <option key={p.passportNumber + sidx} value={sidx}>
                      {p.title} {p.firstName} {p.lastName} ({p.passportNumber})
                    </option>
                  ))}
                </select>
              </div>
              {/* Edit button for saved passengers */}
              {isSaved && !isEditing && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-primary text-xs sm:text-sm font-semibold hover:underline transition"
                    onClick={() =>
                      setEditingOverride((prev) =>
                        prev.map((v, j) => (j === idx ? true : v))
                      )
                    }
                  >
                    ✏️ Edit Passenger Info
                  </button>
                </div>
              )}
              <PassengerForm
                index={idx + 1}
                type={type}
                value={passengerData}
                onChange={(data) => handlePassengerChange(idx, data)}
                disabled={!!isSaved && !isEditing}
                departureDate={departureDate}
                currency={currency}
              />
              {(!isSaved || isEditing) && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                  <input
                    type="checkbox"
                    id={`saveToProfile-${idx}`}
                    checked={saveToProfile[idx]}
                    onChange={(e) =>
                      setSaveToProfile((prev) =>
                        prev.map((v, j) => (j === idx ? e.target.checked : v))
                      )
                    }
                    className="mt-1 sm:mt-0"
                  />
                  <label
                    htmlFor={`saveToProfile-${idx}`}
                    className="text-xs sm:text-sm text-gray-700"
                  >
                    Save this passenger to profile
                  </label>
                  {saveToProfile[idx] && (
                    <button
                      className="bg-primary text-white px-3 py-1 rounded font-semibold hover:bg-blue-700 transition text-xs sm:text-sm w-full sm:w-auto"
                      type="button"
                      onClick={() => handleSaveToProfile(idx)}
                    >
                      Save
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [checklistSelected, setChecklistSelected] = useState<ChecklistItem[]>([]);
  const [checklistAddonTotal, setChecklistAddonTotal] = useState(0);

  // Chargeable seats the customer picked are assigned in the PNR AND must be
  // added to the amount charged. `amount` is the flight fare (base+taxes+markup);
  // the grand total is what we display and send to /create-order.
  const seatTotal = seatSelections.reduce((sum, s) => sum + (s.amount || 0), 0);
  /** Everything chargeable. */
  const quotedTotal = Number(
    (amount + seatTotal + checklistAddonTotal).toFixed(2),
  );

  const grandTotal = quotedTotal;

  const destinationAirport = (() => {
    const legs: any[] = flightData?.legs ?? [];
    const outboundLeg = legs.find((l: any) => l.direction === "outbound") ?? legs[0];
    return (
      outboundLeg?.arrival?.airport?.code ||
      flightData?.onwardFlight?.schedules?.[0]?.arrival?.airport ||
      undefined
    );
  })();

  const bookingDetails = {
    flightData,
    passengerCounts,
    departureDate,
    returnDate,
    tripType,
    multicitySegmentDates,
    classOfService: location.state?.classOfService || "Y",
    selectedClass: location.state?.selectedClass || "w",
    totalAmount: quotedTotal,
    seatTotal,
    checklistItems: checklistSelected,
    checklistAddonTotal,
  };

  const handleProceed = () => {
    setShowReviewPopup(true);
  };

  return (
    <div
      className={`${styles.boxWidth} pt-6 sm:pt-10 space-y-6 sm:space-y-8 pb-12 sm:pb-20`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2">
          {tripType === "roundtrip" && returnProps ? (
            <RoundTripFlightCard
              onward={onwardProps}
              returnSegment={returnProps}
              amount={amount}
              currency={currency}
              baseFare={baseFare}
              taxes={taxes}
              classOfService={classOfService}
              selectedClass={selectedClass}
              flightData={flightData}
            />
          ) : tripType === "multicity" ? (
            <MultiCityFlightCard
              itinerary={flightData}
              selectedFareTierIndex={location.state?.selectedFareTierIndex ?? 0}
              showFareSelection={false}
              showBookButton={false}
              expandedByDefault={true}
              disableResetOnCollapse={true}
              displayAdtFareOnly={true}
              classOfService={classOfService}
              selectedClass={selectedClass}
            />
          ) : (
            <FlightCard
              onward={onwardProps}
              amount={amount}
              baseFare={baseFare}
              taxes={taxes}
              currency={currency}
              classType={classOfService}
              selectedClass={selectedClass}
              flightData={flightData}
              fareTiers={[]}
            />
          )}
        </div>
        <div className="lg:col-span-1 space-y-4">
          <div className="flex flex-col w-full">
            <div className="flex flex-col justify-between w-full p-4 sm:p-4 rounded-2xl shadow-md bg-[#F5F8FF]">
              <div className="flex justify-between items-center border-b mb-2 pb-2">
                <p className="font-bold text-base sm:text-lg">Fare Summary</p>
                <FaClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span>Base Fare:</span>
                  <span>
                    {currency} {baseFare}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Taxes & Fees:</span>
                  <span>
                    {currency} {taxes}
                  </span>
                </div>
                {serviceFee > 0 && (
                  <div className="flex justify-between text-primary">
                    <span>Service Fee:</span>
                    <span>
                      {currency} {serviceFee}
                    </span>
                  </div>
                )}
                {seatTotal > 0 && (
                  <div className="flex justify-between text-primary">
                    <span>Seats:</span>
                    <span>
                      {currency} {seatTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {checklistAddonTotal > 0 && (
                  <div className="flex justify-between text-primary">
                    <span>Add-ons:</span>
                    <span>
                      {currency} {checklistAddonTotal}
                    </span>
                  </div>
                )}
                <div className="pt-2 flex justify-between font-sora text-lg sm:text-xl font-bold text-black">
                  <span>Total:</span>
                  <span>
                    {currency} {grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <FareRulesDisplay
            flightData={flightData}
            passengerCounts={passengerCounts}
            departureDate={departureDate}
            returnDate={returnDate}
            currency={currency}
            revalId={contextRevalId || flightData?.revalId}
            sessionId={contextSessionId}
            userId={user?.uid}
            displayMode="popup"
            variant="compact"
          />

          {/* ── Seat Selection ── */}
          {seatsEnabled && revalId && user?.uid && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowSeatMap(true)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-semibold transition ${
                  seatSelections.length > 0
                    ? "bg-primary/5 border-primary/30 text-primary"
                    : "bg-white border-gray-200 text-gray-700 hover:border-primary/40 hover:text-primary"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="2" width="4" height="6" rx="1" /><rect x="10" y="2" width="4" height="6" rx="1" /><rect x="16" y="2" width="4" height="6" rx="1" />
                    <rect x="4" y="10" width="4" height="6" rx="1" /><rect x="10" y="10" width="4" height="6" rx="1" /><rect x="16" y="10" width="4" height="6" rx="1" />
                    <rect x="4" y="18" width="4" height="4" rx="1" /><rect x="10" y="18" width="4" height="4" rx="1" /><rect x="16" y="18" width="4" height="4" rx="1" />
                  </svg>
                  Select Seats
                  {seatSelections.length > 0 && (
                    <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                      {seatSelections.length} selected
                    </span>
                  )}
                </span>
                <span className="text-xs text-gray-400">Optional</span>
              </button>

              <SeatMap
                isOpen={showSeatMap}
                onClose={() => setShowSeatMap(false)}
                userId={user.uid}
                revalId={revalId}
                sessionId={contextSessionId || undefined}
                passengerCounts={passengerCounts || { ADT: 1 }}
                currency={currency}
                onConfirm={(selections) => setSeatSelections(selections)}
              />
            </div>
          )}

          {/* ── Pre-Booking Checklist ── */}
          {/* <div className="mt-4">
            <PreBookingChecklist
              destinationCode={destinationAirport}
              onChange={(selected, total) => {
                setChecklistSelected(selected);
                setChecklistAddonTotal(total);
              }}
            />
          </div> */}
        </div>
      </div>
      <div className="space-y-8 sm:space-y-12">
        {renderPassengerForms("Adult", "ADT", passengerCounts?.ADT || 0)}
        {renderPassengerForms("Child", "CHD", passengerCounts?.CHD || 0)}
        {renderPassengerForms("Infant", "INF", passengerCounts?.INF || 0)}
      </div>
      <ProceedToPaymentButton totalAmount={grandTotal} onClick={handleProceed} />
      {showReviewPopup && (
        <ReviewAndPayPopup
          passengerData={passengers}
          bookingDetails={bookingDetails}
          seatSelections={seatSelections}
          onClose={() => setShowReviewPopup(false)}
        />
      )}
    </div>
  );
};

export default PassengerDetail;
